import type { ElementType } from "react"
import { memo } from "react"

import { useTranslations } from "next-intl"

import { ImpressionTarget } from "@/components/marketplace/impression-target"
import { Price } from "@/components/marketplace/product/price"
import { WishlistHeart } from "@/components/marketplace/product/wishlist-heart"
import type { Product } from "@/components/marketplace/types"
import { Cents, Chip, DiscountBadge, RatingStarIcon, Skeleton, StarIcon } from "@/components/ui"
import { PRODUCT_PHOTO_QUALITY, TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"
import { formatMoney, isPositiveMoney } from "@/lib/money"
import { tv, type VariantProps } from "@/lib/utils/tv"

// One product card, packshot-led. The square photo tile IS the card — a rounded
// tile lifted off the grey canvas by a soft shadow, the packshot fitted whole inside it. Merch
// signals ride on the image where they differentiate (the markdown/new sello
// top-left, the wishlist heart bottom-right); the value chips (no-interest, or a
// secured plan's "more time", and free shipping) sit under the body. Price leads, then the name, then the quincena line
// with the rating folded into it, then the chips.
const card = tv({
  slots: {
    wrap: "@container relative isolate flex h-full min-w-0 flex-col",
    root: "group relative isolate flex h-full flex-col",
    media: "rounded-10x bg-surface-paper shadow-tile relative aspect-square overflow-hidden @card:rounded-12x",
    image: "object-contain",
    // Sello top-left over the packshot, which carries its own stacking context so a
    // white-background photo can't paint over it.
    sello: "absolute top-250 left-250 z-10 flex",
    // The heart sits at the picture's bottom-right. This layer mirrors the square
    // media (top, full width) so the heart anchors to the image, not the whole card;
    // it stays a sibling of the link (a button inside an anchor is invalid) and lets
    // image clicks fall through to the link (pointer-events).
    heartLayer: "pointer-events-none absolute inset-x-0 top-0 z-10 aspect-square",
    heart: "pointer-events-auto absolute right-250 bottom-250 @card:right-300",
    body: "flex flex-1 flex-col pt-100 @card:pt-250",
    price: "flex items-baseline gap-200 pt-75 @card:pt-0",
    title: "line-clamp-1 text-fg-title font-text text-caption-lg leading-detail @card:line-clamp-2 @card:text-body-sm",
    meta: "font-text text-fg-brand text-little flex min-w-0 flex-col gap-y-50 font-medium tabular-nums @card:mt-100 @card:min-h-5 @card:items-start @card:gap-x-100 @card:text-little-lg",
    rating: "text-fg-heading inline-flex shrink-0 items-center gap-100 font-normal @card:text-fg-brand @card:gap-50 @card:font-medium",
    ratingDot: "hidden @card:mr-50 @card:inline",
    star: "shrink-0",
    chips: "flex flex-wrap items-center gap-125 pt-150 @card:pt-300",
  },
  variants: {
    size: {
      compact: { sello: "top-150 left-150" },
      default: {},
    },
    // Motion is scroll + tap: the tap press-scale reacts, no resting-shadow lift.
    interactive: {
      true: { root: "press focus-visible:ring-brand rounded-10x cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none @card:rounded-12x" },
    },
    tone: {
      default: {},
      inverse: { title: "text-fg-inverted", meta: "text-fg-inverted" },
    },
    // A secured line ("Desde $1,099.00 por pago") outgrows a card-width row, so it may wrap
    // and the rating stacks under it as on a phone; the short regular line keeps the rating beside it.
    stacked: {
      true: { ratingDot: "@card:hidden" },
      false: { meta: "@card:flex-row" },
    },
  },
  defaultVariants: { size: "default", tone: "default", stacked: false },
})

// A single review is a review, not a rating: below this floor the stars say more
// than the catalog knows, so the card shows nothing at all.
const MIN_REVIEWS_FOR_RATING = 5

interface ProductCardProps {
  product: Product
  href?: string
  featureBadge?: string
  // Accepted for call-site compatibility; the redesigned card no longer paints a rank.
  rank?: number
  size?: VariantProps<typeof card>["size"]
  tone?: VariantProps<typeof card>["tone"]
  priority?: boolean
  exactPrice?: boolean
  hidePrice?: boolean
  onClick?: () => void
}

export const ProductCard = memo(function ProductCard({
  product,
  href = product.href,
  featureBadge,
  size = "default",
  tone = "default",
  priority = false,
  exactPrice = false,
  hidePrice = false,
  onClick,
}: ProductCardProps) {
  const t = useTranslations("marketplace")
  const tPdp = useTranslations("pdp")
  const discount = product.discount
  const interestFree = product.chips?.includes("noint")
  const freeship = product.chips?.includes("freeship")
  const installmentCount = product.installments ?? 4
  // A secured plan quotes only its recurring payment ("Desde $X por pago"): its term and cadence
  // vary by plan, so the card leaves both to the product page.
  const secured = product.financingMode === "SECURED"
  // api-v2 omits a zero down payment, so a secured plan without one has none.
  const securedDownPayment = product.downPayment && isPositiveMoney(product.downPayment) ? product.downPayment : null
  const showRating = product.rating != null && (product.reviewCount ?? MIN_REVIEWS_FOR_RATING) >= MIN_REVIEWS_FOR_RATING
  const dense = size === "compact"
  const showNew = (product.isNew ?? false) && !featureBadge
  const sello = featureBadge ? (
    <Chip tone="lightning" size="sm" className="tabular-nums">
      <StarIcon size={11} />
      {featureBadge}
    </Chip>
  ) : showNew ? (
    <Chip tone="new" size="sm">
      {t("product.new")}
    </Chip>
  ) : discount ? (
    <DiscountBadge tone="red" size={dense ? "xs" : "sm"}>
      {`-${discount}%`}
    </DiscountBadge>
  ) : null
  const styles = card({ size, tone, interactive: Boolean(href), stacked: secured })
  const Root: ElementType = href ? Link : "article"
  return (
    <ImpressionTarget data-testid="product-card" data-nelo-item={product.id} serveId={product.serveId} className={styles.wrap()}>
      <Root {...(href ? { href, prefetch: true } : {})} onClick={onClick} className={styles.root()}>
        <div className={styles.media()}>
          <TileImage
            src={product.image}
            alt=""
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 30vw, (max-width: 1536px) 320px, 240px"
            priority={priority}
            quality={PRODUCT_PHOTO_QUALITY}
            className={styles.image()}
          />
          {sello && <div className={styles.sello()}>{sello}</div>}
        </div>
        <div className={styles.body()}>
          {!hidePrice && (
            <div className={styles.price()}>
              <Price
                amount={product.price}
                was={showNew ? undefined : product.was}
                size="product"
                weight="semibold"
                tone={tone === "inverse" ? "inverse" : "ink"}
                exact={exactPrice}
              />
            </div>
          )}
          <div className={styles.title()}>{product.title}</div>
          {(product.payments != null || (showRating && product.rating != null)) && (
            <div className={styles.meta()}>
              {product.payments != null &&
                (secured ? (
                  <>
                    <span>{t.rich("bnpl.securedFromPayment", { amount: formatMoney(product.payments), b: (chunks) => <Cents>{chunks}</Cents> })}</span>
                    <span>
                      {securedDownPayment
                        ? t.rich("bnpl.securedDownPayment", { amount: formatMoney(securedDownPayment), b: (chunks) => <Cents>{chunks}</Cents> })
                        : t("bnpl.securedNoDownPayment")}
                    </span>
                  </>
                ) : (
                  <span>{t.rich("bnpl.installmentsLine", { count: installmentCount, amount: formatMoney(product.payments), b: (chunks) => <Cents>{chunks}</Cents> })}</span>
                ))}
              {showRating && product.rating != null && (
                <span className={styles.rating()}>
                  {product.payments != null && (
                    <span aria-hidden className={styles.ratingDot()}>
                      ·
                    </span>
                  )}
                  {product.rating.toFixed(1)}
                  <RatingStarIcon size={9} className={styles.star()} />
                </span>
              )}
            </div>
          )}
          <div className={styles.chips()}>
            {secured ? (
              <Chip tone="moretime" size="detail">
                {tPdp("priceHero.moreTime")}
              </Chip>
            ) : (
              interestFree && (
                <Chip tone="nointerest" size="detail">
                  {t("product.noInterest")}
                </Chip>
              )
            )}
            {freeship && (
              <Chip tone="freeship" size="detail">
                {t("product.freeShipping")}
              </Chip>
            )}
          </div>
        </div>
      </Root>
      <div className={styles.heartLayer()}>
        <WishlistHeart productId={product.id} className={styles.heart()} />
      </div>
    </ImpressionTarget>
  )
})

export const ProductCardSkeleton = () => (
  <div className="@container flex flex-col">
    <Skeleton shape="card" className="rounded-10x @card:rounded-12x aspect-square w-full" />
    <div className="@card:pt-250 flex flex-col gap-100 pt-400">
      <Skeleton className="h-6 w-2/5" />
      <Skeleton className="h-4 w-5/6" />
      <Skeleton className="h-4 w-4/6" />
      <Skeleton className="mt-50 h-5 w-3/5" />
      <Skeleton className="mt-100 h-6 w-1/2" />
    </div>
  </div>
)
